import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Autocomplete,
  FormControl,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import AddToPhotosOutlinedIcon from "@mui/icons-material/AddToPhotosOutlined";
import { useSelector, useDispatch } from "react-redux";
import {
  selectAllUsers,
  selectIsLoading,
  selectUserToken,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../../features/userSlice";
import { DataAccuracyEndPointLogic } from "../../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import store from "../../../../../store";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setIsSuccess,
  setReloadKey,
  setSelectedRowsData,
} from "../../../../../features/dataAccuracySlice";

const AssignDataAccuracy = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [assigneeName, setAssigneeName] = useState<any>(null);
  const [assigneeId, setAssigneeId] = useState<any>(null);
  const [reason, setReason] = useState<string>("");
  const { selectedRowsData, isSuccess } = useSelector(
    (state: any) => state.dataAccuracy
  );

  const userToken = useSelector(selectUserToken);
  const loading = useSelector(selectIsLoading);
  const allUsers = useSelector(selectAllUsers);
  const [selected, setSelected] = useState<any[]>([]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      setSelected(selectedRowsData || []);
    }
  }, [selectedRowsData, open]);

  const isAllSelected =
    selected.length > 0 && selected.length === selectedRowsData.length;

  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRowsData]);
  };

  const handleAssigneeChange = (value: any) => {
    setAssigneeId(value?.id || null);
    setAssigneeName(value || null);
  };

  useEffect(() => {
    DataAccuracyEndPointLogic.getAllUserList(userToken);
  }, [userToken]);

  const assignBody = {
    tasks: selected.map((task) => ({
      shared_uid: task.shared_uid,
      assignee: assigneeId,
      reason: reason?.trim(),
    })),
  };

  const clearLocalState = () => {
    setSelected([]);
    setAssigneeId(null);
    setAssigneeName(null);
    setReason("");
  };

  const callAPIs = async () => {
    if (selected.length > 0 && assigneeId) {
      store.dispatch(setIsLoading(true));

      try {
        await DataAccuracyEndPointLogic.updateDataAccuracyTask(
          userToken,
          assignBody,
          dispatch
        );

        clearLocalState();
        store.dispatch(setReloadKey(true));
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Assign Task Success",
            type: "success",
          })
        );

        onClose();
      } catch (error) {
        console.error("Assign API error:", error);
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Assign failed. Please try again.",
            type: "error",
          })
        );
      } finally {
        store.dispatch(setIsLoading(false));
      }
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Please select tasks, an assignee, and provide a reason.",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(setSelectedRowsData([]));
      dispatch(setIsSuccess(false));
    }
  }, [isSuccess, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      {loading && <SimpleBackdrop />}
      <DialogTitle>Assign Tasks</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "8px" }}>
          Selected Tasks
        </Typography>
        <Paper sx={{ width: "100%", overflowY: "auto", height: "225px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Task Name</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRowsData.map((row: any, index: any) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  <TableCell>{row.task_structure__title}</TableCell>
                  <TableCell>
                    {row?.data["meta_data:website_name"]?.value}
                  </TableCell>
                  <TableCell>
                    {row?.data["query_output:brand_name"]?.value}
                  </TableCell>
                  <TableCell>
                    <DataStatusAlert status={row.current_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ paddingTop: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl fullWidth margin="normal">
              <TextField
                label="Owner"
                value="Netanel"
                disabled
                size="small"
                required
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                value={assigneeName}
                onChange={(_, newValue) => handleAssigneeChange(newValue)}
                options={allUsers}
                getOptionLabel={(option) =>
                  option?.first_name + " " + option?.last_name
                }
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Assignee"
                    size="small"
                    required
                    color="secondary"
                  />
                )}
              />
            </FormControl>
          </Box>

          <Box display="flex" gap={2} alignItems="center" marginTop={2}>
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              color="secondary"
              fullWidth
            />
          </Box>
        </Box>

        <Typography
          gutterBottom
          sx={{ fontSize: "12px", paddingBottom: "2px", color: "#666666" }}
        >
          All fields are mandatory
        </Typography>
      </DialogContent>
      <DialogActions sx={{ marginBottom: "16px", paddingRight: "40px" }}>
        <Button
          onClick={() => {
            if (!loading) {
              clearLocalState();
              onClose();
            }
          }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="secondary"
          sx={{ whiteSpace: "nowrap", width: "140px" }}
          disabled={loading || !assigneeId || selected.length === 0}
          onClick={callAPIs}
          data-tracker="170"
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <>
              Assign
              <AddToPhotosOutlinedIcon
                sx={{ color: "#fff", marginLeft: "5px" }}
                fontSize="small"
              />
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDataAccuracy;
